import { FORBIDDEN, UNAUTHORIZED } from '~/constants/http'
import { HttpClientProxy } from '~/utils/http'
import { getNewToken } from '~/utils/auth'
import { mapStores } from 'pinia'
import useAuthStore from '~/stores/auth'
import { addWatermarkInPDF } from '~/src/utils/pdf'
import { getVimeoData } from '~/src/utils/vimeo'
import { logError } from '~/utils/log-error'

export default {
  props: {
    fileInfo: {
      type: Object,
      default: null,
    },
    category: {
      type: String,
      default: null,
    },
    downloadingClasses: { type: String, default: '' },
    downloadButtonVariant: {
      type: String,
      default: 'primary',
    },
  },

  data() {
    return {
      downloading: false,
      downloadProgress: 0,
      cancellationSource: null,
      retries: 0,
    }
  },

  computed: {
    ...mapStores(useAuthStore),
    isAlreadyAuthenticated() {
      return this.authStore.authenticated
    },
    fullName() {
      return this.authStore.fullName
    },
    progressBarDownloadingStyles() {
      return {
        width: `${this.downloadProgress > 5 ? this.downloadProgress : 0}%`,
      }
    },

    progressBarDownloadingStylesForCard() {
      return {
        width: `${this.downloadProgress > 5 ? this.downloadProgress : 0}%`,
        bottom: 0,
        borderRadius: 0,
      }
    },
    userEmail() {
      return this.authStore.user.email
    },
  },

  methods: {
    async downloadReport(report, category, onErrorCallback = null) {
      if (!report.file_name) return
      const id = report.id || report.ID
      await this.downloadReportRequest(
        `${this.$config.public.apiRestEndpoint}/downloads/category/${category}/${id}`,
        report.file_name,
        onErrorCallback,
        category,
      )
    },

    async downloadReportById(id, onErrorCallback = null) {
      await this.downloadReportRequest(
        `${this.$config.public.apiRestEndpoint}/downloads/report/${id}`,
        null,
        onErrorCallback,
      )
    },

    async downloadReportByIds(ids, onErrorCallback = null) {
      await Promise.all(
        ids.map(async (id) => {
          await this.downloadReportRequest(
            `${this.$config.public.apiRestEndpoint}/downloads/report/${id}`,
            null,
            onErrorCallback,
            null,
            true,
          )
        }),
      )
    },

    extractFilenameFromHeaders(headers) {
      const rawFilename = headers['content-disposition']
      return rawFilename.split('filename=')[1]
    },

    async downloadReportRequest(
      url,
      filename = null,
      onErrorCallback = null,
      category = null,
      isMultiple = false,
    ) {
      if (this.downloading) return

      this.downloading = !isMultiple

      const options = await HttpClientProxy.buildHttpOptions(
        {},
        this.$cookies,
        this.$config,
      )
      this.abortController = new AbortController()

      await this.$axios({
        method: 'POST',
        url,
        responseType: 'blob',
        headers: options.headers,
        signal: this.abortController.signal,
        onDownloadProgress: (progressEvent) => {
          const total = parseFloat(progressEvent.total)
          const current = progressEvent.loaded
          this.downloadProgress = Math.floor((current / total) * 100)
        },
      })
        .then(async ({ data: blob, headers }) => {
          this.retries = 0
          const isPDF =
            this.extractFilenameFromHeaders(headers)?.endsWith('.pdf')
          let blobFile = blob
          if (isPDF && category !== 'public-documents') {
            const pdfWithWatermark = await this.addWatermarkInPDF(blob)
            blobFile = new Blob([pdfWithWatermark], {
              type: 'application/pdf',
            })
          }
          const blobUrl = window.URL.createObjectURL(blobFile)
          const a = document.createElement('a')
          a.style.display = 'none'
          a.href = blobUrl
          a.download = filename || this.extractFilenameFromHeaders(headers)
          document.body.appendChild(a)
          a.click()
          window.URL.revokeObjectURL(blobUrl)
          document.body.removeChild(a)

          this.$gtag.event('ce_file_download', {
            event_category: 'download',
            value: filename,
            send_to: 'default',
          })
        })
        .catch(async (e) => {
          if (e.status === FORBIDDEN || e.response?.status === FORBIDDEN) {
            await this.$membershipApi.getActiveMemberships()

            if (onErrorCallback && typeof onErrorCallback === 'function') {
              onErrorCallback()
            }
          }

          if (e.response?.status === UNAUTHORIZED && this.retries < 1) {
            await getNewToken({
              $cookies: this.$cookies,
              $config: this.$config,
            })
            this.downloading = false
            this.retries++
            await this.downloadReportRequest(url, filename, onErrorCallback)
          }
        })
        .finally(() => {
          this.resetDownloadState()
        })
    },

    async addWatermarkInPDF(blob) {
      return addWatermarkInPDF(blob)
    },

    async getVimeoData(vimeoId) {
      if (this.downloading) return
      this.downloading = true
      return await getVimeoData({
        vimeoId,
        downloadProgress: this.downloadProgress,
      })
    },

    async downloadVideoVimeo(vimeoId) {
      try {
        const data = await this.getVimeoData(vimeoId)
        const downloadUrl = data.download.find(
          (download) =>
            download.quality === 'hd' && download.rendition === '720p',
        )?.link
        if (downloadUrl) {
          this.downloadVideo(downloadUrl)
        }
      } catch (_error) {
        logError(_error)
      } finally {
        this.resetDownloadState()
      }
    },

    downloadVideo(url) {
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },

    resetDownloadState() {
      this.downloading = false
      this.downloadProgress = 0
      this.abortController = null
    },

    cancelDownload() {
      if (!this.abortController) return

      this.abortController.abort()
      this.resetDownloadState()
    },
  },
}
